"use client";
import React, { useState, useMemo } from 'react';
import { ScheduledCourse } from '../types/course';
import { XMarkIcon, ExclamationTriangleIcon, CheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { getCampusColor } from '../types/colors';
import Modal from './Modal';
import { formatBaseIdDisplay } from '../lib/utils';

interface OverlapConflict {
  course1: ScheduledCourse;
  course2: ScheduledCourse;
  day: string;
  timeConflict: string;
  alternativeSections1?: ScheduledCourse[];
  alternativeSections2?: ScheduledCourse[];
}

interface OverlapResolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  conflicts: OverlapConflict[];
  allSectionsByCourseId: { [courseId: string]: ScheduledCourse[] };
  onResolveConflict: (conflictIndex: number, resolution: 'course1' | 'course2' | 'both') => void;
  onReplaceSection: (courseId: string, newSectionId: string) => void;
}

const OverlapResolutionModal: React.FC<OverlapResolutionModalProps> = ({
  isOpen,
  onClose,
  conflicts,
  allSectionsByCourseId,
  onResolveConflict,
  onReplaceSection
}) => {
  const [selectedConflict, setSelectedConflict] = useState<number>(0);
  const [resolvedConflicts, setResolvedConflicts] = useState<Set<number>>(new Set());

  const getCampusStyle = (campus: string) => {
    return getCampusColor(campus, 'solid');
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const getAlternativeSections = (course: ScheduledCourse): ScheduledCourse[] => {
    const baseId = course.baseId;
    const allSections = allSectionsByCourseId[baseId] || [];
    return allSections.filter(section => section.id !== course.id);
  };

  const handleResolveConflict = (conflictIndex: number, resolution: 'course1' | 'course2' | 'both') => {
    onResolveConflict(conflictIndex, resolution);
    setResolvedConflicts(prev => new Set([...prev, conflictIndex]));
  };

  const handleReplaceSection = (courseId: string, newSectionId: string) => {
    onReplaceSection(courseId, newSectionId);
  };

  const currentConflict = conflicts[selectedConflict];
  const alternatives1 = currentConflict ? getAlternativeSections(currentConflict.course1) : [];
  const alternatives2 = currentConflict ? getAlternativeSections(currentConflict.course2) : [];

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      showCloseButton={false}
      closeOnOverlayClick={false}
      ariaLabel="Resolve Class Conflicts"
    >
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Resolve Class Conflicts
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close conflict resolution"
            onClick={onClose}
            className="grid min-h-12 min-w-12 place-items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          {/* Conflict Navigation */}
          {conflicts.length > 1 && (
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex space-x-2 overflow-x-auto">
                {conflicts.map((conflict, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedConflict(index)}
                    className={`px-3 py-2 rounded-control text-sm font-medium whitespace-nowrap ${
                      selectedConflict === index
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                        : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    Conflict {index + 1}
                    {resolvedConflicts.has(index) && (
                      <CheckIcon className="inline h-4 w-4 ml-1" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conflict Details */}
          <div className="flex-1 overflow-y-auto p-6">
            {currentConflict && (
              <div className="space-y-6">
                {/* Conflict Summary */}
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-control p-4">
                  <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                    Time Conflict on {currentConflict.day}
                  </h3>
                  <p className="text-red-700 dark:text-red-300">
                    {currentConflict.timeConflict}
                  </p>
                </div>

                {/* Conflicting Courses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Course 1 */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-control p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-900 dark:text-white">
                        {currentConflict.course1.name}
                      </h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium text-white ${getCampusStyle(currentConflict.course1.campus)}`}>
                        {currentConflict.course1.campus}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                      <div>{formatBaseIdDisplay(currentConflict.course1.baseId)} • Section {currentConflict.course1.section}</div>
                      <div>Credits: {currentConflict.course1.credits}</div>
                      {currentConflict.course1.instructors && (
                        <div>Instructor: {currentConflict.course1.instructors.join(', ')}</div>
                      )}
                      {currentConflict.course1.meetingTimes && (
                        <div>
                          Times: {currentConflict.course1.meetingTimes.map(mt => 
                            `${mt.day} ${formatTime(mt.startMinute)}-${formatTime(mt.endMinute)}`
                          ).join(', ')}
                        </div>
                      )}
                    </div>

                    {/* Alternative Sections */}
                    {alternatives1.length > 0 && (
                      <div className="mt-4">
                        <h5 className="font-medium text-slate-900 dark:text-white mb-2">Alternative Sections:</h5>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {alternatives1.slice(0, 5).map((section) => (
                            <button
                              key={section.id}
                              onClick={() => handleReplaceSection(currentConflict.course1.id, section.id)}
                              className="w-full text-left p-2 rounded border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm"
                            >
                              <div className="font-medium">{section.section}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                {section.meetingTimes?.map(mt => 
                                  `${mt.day} ${formatTime(mt.startMinute)}-${formatTime(mt.endMinute)}`
                                ).join(', ')}
                              </div>
                            </button>
                          ))}
                          {alternatives1.length > 5 && (
                            <div className="text-xs text-slate-500 dark:text-slate-400 text-center">
                              +{alternatives1.length - 5} more sections
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Course 2 */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-control p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-900 dark:text-white">
                        {currentConflict.course2.name}
                      </h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium text-white ${getCampusStyle(currentConflict.course2.campus)}`}>
                        {currentConflict.course2.campus}
                      </span>
                    </div>
                    
                    <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                      <div>{formatBaseIdDisplay(currentConflict.course2.baseId)} • Section {currentConflict.course2.section}</div>
                      <div>Credits: {currentConflict.course2.credits}</div>
                      {currentConflict.course2.instructors && (
                        <div>Instructor: {currentConflict.course2.instructors.join(', ')}</div>
                      )}
                      {currentConflict.course2.meetingTimes && (
                        <div>
                          Times: {currentConflict.course2.meetingTimes.map(mt => 
                            `${mt.day} ${formatTime(mt.startMinute)}-${formatTime(mt.endMinute)}`
                          ).join(', ')}
                        </div>
                      )}
                    </div>

                    {/* Alternative Sections */}
                    {alternatives2.length > 0 && (
                      <div className="mt-4">
                        <h5 className="font-medium text-slate-900 dark:text-white mb-2">Alternative Sections:</h5>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {alternatives2.slice(0, 5).map((section) => (
                            <button
                              key={section.id}
                              onClick={() => handleReplaceSection(currentConflict.course2.id, section.id)}
                              className="w-full text-left p-2 rounded border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm"
                            >
                              <div className="font-medium">{section.section}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                {section.meetingTimes?.map(mt => 
                                  `${mt.day} ${formatTime(mt.startMinute)}-${formatTime(mt.endMinute)}`
                                ).join(', ')}
                              </div>
                            </button>
                          ))}
                          {alternatives2.length > 5 && (
                            <div className="text-xs text-slate-500 dark:text-slate-400 text-center">
                              +{alternatives2.length - 5} more sections
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Resolution Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => handleResolveConflict(selectedConflict, 'course1')}
                    className="min-h-12 flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-control transition-colors"
                  >
                    Keep {currentConflict.course1.name} (Remove {currentConflict.course2.name})
                  </button>
                  <button
                    onClick={() => handleResolveConflict(selectedConflict, 'course2')}
                    className="min-h-12 flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-control transition-colors"
                  >
                    Keep {currentConflict.course2.name} (Remove {currentConflict.course1.name})
                  </button>
                  <button
                    onClick={() => handleResolveConflict(selectedConflict, 'both')}
                    className="min-h-12 flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-control transition-colors"
                  >
                    Remove Both Classes
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {resolvedConflicts.size} of {conflicts.length} conflicts resolved
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="min-h-12 px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-control transition-colors"
                >
                  Close
                </button>
                {resolvedConflicts.size === conflicts.length && (
                  <button
                    onClick={onClose}
                    className="min-h-12 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-control transition-colors"
                  >
                    All Conflicts Resolved
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OverlapResolutionModal;
